import React, {useEffect, useState} from "react"
import PropTypes from "prop-types"
import {Box, Grid, Paper, TableCell, TableRow, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material"
// import DataTable from "../data-table"
import Plot from "react-plotly.js"
import {v4 as uuidv4} from "uuid"
// import theme from "src/utils/theme"
import dayjs from "dayjs"
import {DATE_FORMAT_SHOW} from "src/utils/constants"
import Plotly from "plotly.js-basic-dist"

const ToggleButtonGroupSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0, // sem espaço entre itens
    // remove margin/borda padrão entre botões agrupados
    '& .MuiToggleButtonGroup-grouped': {
        margin: 0,
        borderRadius: 0,
        borderColor: 'divider',
        borderWidth: 1,
        borderStyle: 'solid',
        '&:not(:first-of-type)': {
            borderLeft: 0, // evita borda dupla
        },
    }
}

const ToggleButtonSx = {
    px: 1.25,
    py: 0.5,
    minHeight: 30,
    bgcolor: '#f5f5f5',         // fundo cinza claro padrão
    '&.Mui-selected': {
        bgcolor: '#fff',       // fundo mais claro quando selecionado
        color: '#000',            // fonte preta
        fontWeight: 'bold',
        '&:hover': {
            bgcolor: '#efefef',     // hover no selecionado
        },
    },
    '&:not(.Mui-selected)': {
        bgcolor: '#e0e0e0',       // ainda mais claro quando não selecionado
        color: '#000',            // fonte mais clara
        '&:hover': {
            bgcolor: '#efefef',     // hover no não selecionado
        },
    },
}

const TableSx = {
    whiteSpace: 'nowrap',
    backgroundImage: "none",
    paddingTop: 0.5,
    paddingBottom: 0.5,
    paddingRight: 1,
}

const LineChartComponent = (props) => {
    const [xAxis, setXAxis] = useState({
        name: props.xTitle ? props.xTitle : "",
        values: [],
    })
    const [dataset, setDataset] = useState([])
    const [removedDataset, setRemovedDataSet] = useState([])
    const [legend, setLegend] = useState([])
    const [itemsId, setItemsId] = useState([])
    const [plotKey, setPlotKey] = useState(Math.random() * 10)

    useEffect(() => {
        generateData()
        // Plotly já vem com zoom/pan/drag embutidos; não precisa registrar plugin
    }, [props])

    const generateData = () => {
        setLegend([])

        if (!props.disableXAxis) {
            setXAxis((prev) => ({...prev, values: props.xValue}))
        }

        const newData = []
        const newItemsId = []

        props.values.forEach((group) => {
            const newLegend = []
            group.items.forEach((item) => {
                const color = generateColor()
                const id = uuidv4()
                const newValue = generateDataset(group.grupo, color, item.values, item.item, id)
                const newLegendValue = generateLegend(color, item.item, id)

                newData.push(newValue)
                newLegend.push(newLegendValue)
                newItemsId.push(id)
            })

            setLegend((prevLegend) => [...prevLegend, {group: group.grupo, items: newLegend}])
        })

        setItemsId(newItemsId)
        // se o X estiver embutido nos pontos, já ordena por X
        setDataset(props.disableXAxis ? ordenarDatasetPorX(newData) : newData)
        console.log(legend)
    }

    const generateLegend = (color, item, id) => {
        return {
            id,
            color,
            item,
            variant: "outlined",
        }
    }

    const generateColor = () => {
        const corHex = Math.floor(Math.random() * 16777215).toString(16)
        return `#${corHex.padStart(6, "0")}`
    }

    const generateDataset = (group, color, values, label, id) => {
        return {
            id,
            label: `${group} ${label}`,
            data: values, // pode ser array simples (y) ou array de pontos {x, y}
            color,
            smoothing: props.tension != null ? props.tension : 0.4, // usado no Plotly como 'smoothing'
        }
    }

    const disableEnableLine = (itemId) => {
        const item = dataset.filter((it) => it.id === itemId)

        if (item.length !== 0) {
            const datasetRemove = dataset.filter((it) => it.id !== itemId)
            const removedItemIds = itemsId.filter((obj) => obj !== itemId)

            setDataset(props.disableXAxis ? ordenarDatasetPorX(datasetRemove) : datasetRemove)
            setRemovedDataSet([...removedDataset, item[0]])
            setItemsId(removedItemIds)
        } else {
            const removedItem = removedDataset.find((it) => it.id === itemId)
            const remainingRemoved = removedDataset.filter((it) => it.id !== itemId)
            const newDataSet = [...dataset, removedItem]

            setDataset(props.disableXAxis ? ordenarDatasetPorX(newDataSet) : newDataSet)
            setRemovedDataSet(remainingRemoved)
            setItemsId([...itemsId, itemId])
        }
    }

    const ordenarDatasetPorX = (datasets) => {
        const copia = [...datasets]
        copia.sort((a, b) => {
            // assume que cada 'data' é [{x, y}, ...] e a primeira posição existe
            const dataA = dayjs(a.data?.[0]?.x, DATE_FORMAT_SHOW)
            const dataB = dayjs(b.data?.[0]?.x, DATE_FORMAT_SHOW)
            return dataA - dataB
        })
        return copia
    }

    // --- Mapeia seus datasets para 'traces' do Plotly ---
    const traces = dataset.map((ds) => {
        const hasXY = Array.isArray(ds.data) && typeof ds.data?.[0] === "object" && ds.data?.[0] !== null && "x" in ds.data[0] && "y" in ds.data[0]
        const x = hasXY ? ds.data.map((p) => p.x) : xAxis.values
        const y = hasXY ? ds.data.map((p) => p.y) : ds.data

        return {
            x,
            y,
            type: "scatter",
            mode: "lines+markers",
            name: ds.label,
            connectgaps: true,
            line: {
                color: ds.color,
                width: 2,
                shape: "spline", // curva suave (equivalente ao 'tension')
                smoothing: Math.max(0, Math.min(1.3, ds.smoothing)), // Plotly aceita 0..1.3
            },
            marker: {size: 6, color: ds.color},
            hovertemplate: `${ds.label}<br>${xAxis.name || "x"}: %{x}<br>${props.yTitle || "y"}: %{y}<extra></extra>`,
        }
    })

    // --- Layout do Plotly (títulos, eixos, zoom, etc) ---
    const fontAxis = {
        family: "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
        size: 12
    }

    const layout = {
        autosize: true,
        title: {text: props.title || "", font: {size: 16}},
        margin: {l: 60, r: 20, t: 40, b: 60},
        xaxis: {
            title: {
                text: xAxis.name,
                standoff: 15,
                font: fontAxis,
                yref: "paper",
            },
            type: "category",
            categoryorder: "array",
            categoryarray: props.xValue || [],
            tickmode: "array",
            tickvals: props.xValue || [],
            ticktext: props.xValue || [],
            range: [-0.5, (props.xValue?.length ?? 0) - 0.5],
            tickangle: 0,
            ticklabelposition: "outside",
            automargin: true,
            zeroline: false,
            linewidth: 1,
            ticklen: 6,
            tickwidth: 1,
            showline: true,
            linecolor: "#ccc",
            gridcolor: "#eee",
            ticks: "outside",
        },
        yaxis: {
            title: {
                text: props.yTitle || "",
                font: fontAxis,
                yref: 'paper',
                automargin: true,
            },
            ticks: "outside",
            ticklen: 6,
            tickwidth: 1,
            automargin: true,
            showline: true,
            linewidth: 1,
            linecolor: "#ccc",
            gridcolor: "#eee",
            zeroline: false,
            showgrid: true,
            autorange: true,
        },
        legend: {
            orientation: 'h',
            y: -0.2,
            font: {
                size: 14,
                family: 'Arial, sans-serif',
                color: '#000',
            }
        },
        showlegend: false,
        hovermode: 'closest',
        dragmode: "zoom",
    }

    // const tableBody = legend.map((row, index) => (
    //     <TableRow key={`table-${index}`} index={index}>
    //         <TableCell>{row.group}</TableCell>
    //         {row.items.map((item, indexItem) => (
    //             <TableCell key={`table-${indexItem}`}>
    //                 <Button
    //                     sx={{color: "#000", borderColor: theme.palette.primary.main}}
    //                     id={item.id}
    //                     variant={itemsId.some((obj) => obj === item.id) ? "outlined" : ""}
    //                     onClick={() => disableEnableLine(item.id)}
    //                     startIcon={<SquareRoundedIcon sx={{color: item.color, backgroundColor: item.color}}/>}>
    //                     {item.item}
    //                 </Button>
    //             </TableCell>
    //         ))}
    //     </TableRow>
    // ))

    const gridButtons = legend.map((row, index) => (
        <TableRow key={`table-${index}`} index={index}>
            <TableCell sx={TableSx}>{row.group}</TableCell>

            {/* um único TableCell para TODOS os botões */}
            <TableCell sx={TableSx}>
                <ToggleButtonGroup
                    key={`table-${index}`} index={index}
                    size="small"
                    aria-label={row.group}
                    sx={ToggleButtonGroupSx}
                >
                    {row.items.map((item, indexItem) => (
                        <ToggleButton
                            selected={itemsId.some((obj) => obj === item.id)}
                            key={`${item.id || item.item}-${indexItem}`}
                            value={item.id || item.item}
                            onClick={() => disableEnableLine(item.id)}
                            disableRipple
                            sx={ToggleButtonSx}>
                            <Box component="span" sx={{
                                marginRight: 1,
                                bgcolor: item.color,
                                width: 18,
                                height: 4
                            }}/>
                            <Typography variant="body2">{item.item}</Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </TableCell>
        </TableRow>
    ))

    const plotHeight = props.height ? props.height * 6 : 420
    return (
        <Paper>
            <div id={props.id}>
                <Grid container direction="column" spacing={2} alignItems={"center"} justifyContent={"center"}>
                    {/*<Grid item sm={12} md={12} xl={12} xs={12}>*/}
                    {/*    <DataTable component={null} head={[]} body={tableBody}/>*/}
                    {/*</Grid>*/}
                    <Grid item sm={12} md={12} xl={12} xs={12}>
                        {gridButtons}
                    </Grid>
                </Grid>
                <Plot
                    key={plotKey}
                    data={traces}
                    layout={layout}
                    useResizeHandler
                    style={{width: "100%", height: plotHeight}}
                    config={{
                        modeBarButtonsToRemove: [
                            "select2d",
                            "lasso2d",
                            "zoom2d",
                            "resetScale2d",
                            "zoomIn2d",
                            "zoomOut2d"
                        ],
                        odeBarButtonsToRemove: ["resetScale2d"],
                        responsive: true,
                        displaylogo: false,
                        displayModeBar: true,
                        scrollZoom: true,
                        modeBarButtonsToAdd: [
                            {
                                name: 'Reset Axis',
                                icon: Plotly.Icons.home, // ou outro ícone
                                click: () => {
                                    setPlotKey(Math.random() * 10)
                                },
                            }
                        ]
                    }}
                />
            </div>
        </Paper>
    )
}

LineChartComponent.propTypes = {
    id: PropTypes.string,
    xTitle: PropTypes.string,
    xValue: PropTypes.array,
    yTitle: PropTypes.string,
    title: PropTypes.string,
    values: PropTypes.array, // [{ grupo, items: [{ item, values }] }]; values pode ser [y...] ou [{x,y}...]
    reloadChart: PropTypes.func,
    height: PropTypes.number,
    tension: PropTypes.number, // mapeado para smoothing do Plotly (0..1.3)
    disableXAxis: PropTypes.bool, // true => usa [{x,y}] em cada série
}

export default LineChartComponent